import { createSelector } from 'reselect';
import memoize from 'lodash/memoize';
import type { CardId } from '../../../model/types';
import { isCmpKanbanProject } from '../app';
import { makeIsCardInCollapsedCardGroup } from '../card-entries';
import {
	getVisibleCardListIds,
	makeIsCardListCollapsed,
	makeOrderedVisibleCardIdsForCardList,
	makeGetVisibleCardOrderFromCardEntries,
	makeGetCardListIdByCardId,
	makeIsCardVisible,
} from '../cards';

export const getFirstCardListWithSelectableIssues = createSelector(
	[
		getVisibleCardListIds,
		makeOrderedVisibleCardIdsForCardList,
		makeGetVisibleCardOrderFromCardEntries,
		makeIsCardListCollapsed,
	],
	(
		cardListIds,
		getOrderedVisibleCardsByCardList,
		getVisibleCardOrderFromCardEntries,
		isCardListCollapsed,
	) =>
		cardListIds
			.filter((id) => !isCardListCollapsed(id))
			.map((id) => getVisibleCardOrderFromCardEntries(id))
			.find((cardList) => cardList.length),
);

export const getFirstSelectableIssueAscending = createSelector(
	getFirstCardListWithSelectableIssues,
	(cardList) => cardList && cardList[0],
);

export const getFirstSelectableIssueDescending = createSelector(
	getFirstCardListWithSelectableIssues,
	(cardList) => cardList && cardList[cardList.length - 1],
);

export const makeIsCardVisibleAndNotCollapsed = createSelector(
	[
		makeGetCardListIdByCardId,
		makeIsCardListCollapsed,
		makeIsCardVisible,
		makeIsCardInCollapsedCardGroup,
		isCmpKanbanProject,
	],
	(
		getCardListByCardId,
		isCardListCollapsed,
		isCardVisible,
		isCardInCollapsedCardGroup,
		isCmpKanbanBacklog,
	) =>
		memoize((cardId: CardId) => {
			const cardListId = getCardListByCardId(cardId);
			if (cardListId === undefined) {
				return false;
			}

			if (isCmpKanbanBacklog) {
				return (
					!isCardListCollapsed(cardListId) &&
					!isCardInCollapsedCardGroup(cardId) &&
					isCardVisible(cardId)
				);
			}

			return !isCardListCollapsed(cardListId) && isCardVisible(cardId);
		}),
);

export const getAllSelectableIssues = createSelector(
	[
		getVisibleCardListIds,
		makeOrderedVisibleCardIdsForCardList,
		makeGetVisibleCardOrderFromCardEntries,
		makeIsCardListCollapsed,
	],
	(
		cardListIds,
		getOrderedVisibleCardsByCardList,
		getVisibleCardOrderFromCardEntries,
		isCardListCollapsed,
	) =>
		cardListIds
			.filter((id) => !isCardListCollapsed(id))
			.map((id) => getVisibleCardOrderFromCardEntries(id))
			.flat(),
);

export const makeGetNextSelectableIssue = createSelector(
	getAllSelectableIssues,
	(selectableIssues) =>
		memoize((cardId: CardId) => {
			const cardIndex = selectableIssues.indexOf(cardId);
			if (cardIndex < 0) {
				return undefined;
			}
			return selectableIssues[cardIndex + 1];
		}),
);

export const makeGetPrevSelectableIssue = createSelector(
	getAllSelectableIssues,
	(selectableIssues) =>
		memoize((cardId: CardId) => {
			const cardIndex = selectableIssues.indexOf(cardId);
			return selectableIssues[cardIndex - 1];
		}),
);
